import { useEffect } from 'react';
import { Link, useSearchParams } from 'react-router';
import type { HomeFeedFilter, HomeFeedItem } from '@/api/community';
import { useDocumentTitle } from '@/app/layout/AppShell';
import { useT, tNow } from '@/i18n/languageStore';
import { useSessionStore } from '@/state/sessionStore';
import { Avatar } from '@/ui/kit/Avatar';
import { Button } from '@/ui/kit/Button';
import { SectionTitle } from '@/ui/kit/Card';
import { Badge, Chip } from '@/ui/kit/Chip';
import { Icon } from '@/ui/kit/Icon';
import { PaginationFooter } from '@/ui/kit/PaginationFooter';
import { formatDistanceTier } from '@/features/help-exchange/helpExchangeFormat';
import { formatExpires, formatFeedDistance, timeAgo } from './homeFormat';
import { useCommunityImpact, useHomeFeed, useSpotlight } from './queries';
import { StoryBar } from './StoryBar';
import styles from './home.module.css';
import { TrustSummary } from '@/ui/kit/TrustSummary';

const FILTERS: { key: HomeFeedFilter; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'need', label: 'I need help' },
  { key: 'offer', label: 'I can help' },
  { key: 'promotion', label: 'Promotions' },
];

function isFilter(value: string | null): value is HomeFeedFilter {
  return FILTERS.some((f) => f.key === value);
}

/** `?page=` as a positive whole number, else 1 (spec 008). */
function pageOf(value: string | null): number {
  const n = value !== null && /^\d+$/.test(value) ? Number(value) : 1;
  return Number.isSafeInteger(n) && n >= 1 ? n : 1;
}

/** The list has pages of 20, decided by the server (spec 008). */
const HOME_PAGE_SIZE = [20] as const;

/** A promotion is paid-for: it is labelled "Sponsored" (specs/003 #4), never shown as a neighbour's post. */
const isSponsored = (item: HomeFeedItem) => item.sponsored ?? item.kind === 'promotion';

function kindLabel(item: HomeFeedItem, t: (text: string) => string): string | null {
  if (item.kind === 'promotion') return null;
  return item.kind === 'need' ? t('Needs help') : t('Can help');
}

/** "{kind} · {distance} · {time}"; promotions show the business and end date instead. */
function feedMeta(item: HomeFeedItem, t: (text: string) => string): string {
  const middle =
    item.kind === 'promotion'
      ? item.ownerName
      : (formatDistanceTier(item.distanceTier, null) ?? formatFeedDistance(item.distanceKm));
  const last =
    item.kind === 'promotion' ? t(formatExpires(item.expiresAt)) : timeAgo(item.createdAt);
  return [kindLabel(item, t), middle, last].filter(Boolean).join(' · ');
}

function FooterTile({
  to,
  title,
  subtitle,
  count,
  icon,
  tone,
}: {
  to: string;
  title: string;
  subtitle: string;
  count: number;
  icon: 'tag' | 'calendar';
  tone: 'primary' | 'secondary';
}) {
  const t = useT();
  return (
    <Link to={to} className={styles.footerTile}>
      {count > 0 ? (
        <span className={styles.countBadge} aria-hidden="true">
          {count}
        </span>
      ) : null}
      <span className={styles.footerIcon} data-tone={tone}>
        <Icon name={icon} size={18} />
      </span>
      <span className={styles.rowTitle}>
        {t(title)}
        {count > 0 ? <span className="sr-only">{`, ${t('{n} new', { n: count })}`}</span> : null}
      </span>
      <span className={styles.footerSubtitle}>{t(subtitle)}</span>
    </Link>
  );
}

/**
 * FR-007, FR-049: Home — the two ways to take part, community impact, the
 * neighbour spotlight and the filtered nearby feed (mobile/src/screens/home/HomeScreen.tsx).
 * The filter (`?show=`) and the page (`?page=`) live in the URL so Back and reload keep them.
 */
export function HomePage() {
  const t = useT();
  const member = useSessionStore((s) => s.member);
  const [params, setParams] = useSearchParams();
  const show = params.get('show');
  const filter: HomeFeedFilter = isFilter(show) ? show : 'all';
  const impact = useCommunityImpact();
  const spotlight = useSpotlight().data;
  const page = pageOf(params.get('page'));
  const feed = useHomeFeed(filter, page);
  useDocumentTitle(tNow('Home'));

  // A filter change starts again at page 1.
  function selectFilter(next: HomeFeedFilter) {
    setParams(next === 'all' ? {} : { show: next }, { replace: true });
  }

  function withPage(next: number): URLSearchParams {
    const query = new URLSearchParams(params);
    if (next <= 1) query.delete('page');
    else query.set('page', String(next));
    return query;
  }

  function goToPage(next: number) {
    setParams(withPage(next));
  }

  // When the server answers with another page than asked (past the end), the address shows that page.
  const answeredPage = feed.isPlaceholderData ? undefined : feed.data?.page;
  useEffect(() => {
    if (answeredPage !== undefined && answeredPage !== page) {
      const query = new URLSearchParams(params);
      if (answeredPage <= 1) query.delete('page');
      else query.set('page', String(answeredPage));
      setParams(query, { replace: true });
    }
  }, [answeredPage, page, params, setParams]);

  const firstName = member?.name?.split(' ')[0] || t('neighbor');
  const items = feed.data?.items ?? [];

  return (
    <div className={styles.page}>
      <div className={styles.intro}>
        <h1 className={styles.greeting}>{t('Hi, {name}', { name: firstName })}</h1>
        <p className={styles.subtitle}>{t('What can Kindra help with today?')}</p>
        <StoryBar />
        <div className={styles.ctaRow}>
          <Link to="/posts/new?kind=need" className={styles.cta} data-kind="need">
            <Icon name="user-plus" size={24} />
            <span className={styles.ctaText}>{t('I Need Help')}</span>
          </Link>
          <Link to="/posts/new?kind=offer" className={styles.cta} data-kind="offer">
            <Icon name="heart" size={24} />
            <span className={styles.ctaText}>{t('I Can Help')}</span>
          </Link>
        </div>

        <Link to="/impact" className={styles.impact}>
          <span className={styles.impactIcon}>
            <Icon name="heart" size={19} />
          </span>
          <span className={styles.grow}>
            <span className={styles.rowTitle}>{t('Community impact')}</span>
            <span className={styles.rowMeta}>
              {impact.data
                ? t('{n} neighbors helped this month', { n: impact.data.helpedThisMonth })
                : ' '}
            </span>
          </span>
          <Icon name="chevron-right" size={16} />
        </Link>

        {spotlight ? (
          <section aria-labelledby="home-spotlight">
            <SectionTitle id="home-spotlight">{t('Neighbor spotlight')}</SectionTitle>
            <Link to={`/members/${spotlight.memberId}`} className={styles.spotlight}>
              <Avatar
                name={spotlight.name}
                seed={spotlight.memberId}
                photoUrl={spotlight.photoUrl}
                size={46}
                decorative
              />
              <span className={styles.grow}>
                <span className={styles.nameRow}>
                  <span className={styles.rowTitle}>{spotlight.name}</span>
                  <TrustSummary member={spotlight} />
                </span>
                {spotlight.blurb ? (
                  <span className={styles.blurb}>{tNow(spotlight.blurb)}</span>
                ) : null}
              </span>
            </Link>
          </section>
        ) : null}

        <nav aria-label={t('More from your community')} className={styles.footerRow}>
          <FooterTile
            to="/promotions"
            title={tNow('Promotions')}
            subtitle={tNow('Deals from local businesses')}
            count={feed.data?.promotionsCount ?? 0}
            icon="tag"
            tone="secondary"
          />
          <FooterTile
            to="/events"
            title={tNow('Events')}
            subtitle={tNow('Classes, workshops & cleanups')}
            count={feed.data?.eventsCount ?? 0}
            icon="calendar"
            tone="primary"
          />
        </nav>
      </div>

      <section aria-labelledby="home-nearby" className={styles.feed}>
        <SectionTitle id="home-nearby">{t('Nearby activity')}</SectionTitle>
        <div role="group" aria-label={t('Show')} className={styles.chips}>
          {FILTERS.map((f) => (
            <Chip key={f.key} selected={filter === f.key} onClick={() => selectFilter(f.key)}>
              {t(f.label)}
            </Chip>
          ))}
        </div>
        <div aria-busy={feed.isFetching}>
          {items.length > 0 ? (
            <ul className={styles.feedList}>
              {items.map((item) => (
                <li key={`${item.source}-${item.id}`}>
                  <Link
                    to={item.source === 'post' ? `/posts/${item.id}` : '/promotions'}
                    className={styles.feedRow}
                    data-sponsored={isSponsored(item) || undefined}
                  >
                    <Avatar
                      name={item.ownerName}
                      seed={item.ownerId}
                      photoUrl={item.ownerPhotoUrl}
                      size={42}
                      decorative
                    />
                    <span className={styles.grow}>
                      {isSponsored(item) ? (
                        <span className={styles.sponsored}>{t('Sponsored')}</span>
                      ) : null}
                      {item.status === 'locked' || item.status === 'completed' ? (
                        <span className={styles.progress}>
                          <Badge variant={item.status === 'completed' ? 'success' : 'secondary'}>
                            {item.status === 'completed' ? t('Done') : t('Processing')}
                          </Badge>
                        </span>
                      ) : null}
                      <span className={styles.rowTitle}>{item.title}</span>
                      <span className={styles.rowMeta}>{tNow(feedMeta(item, t))}</span>
                    </span>
                    <Icon name="chevron-right" size={16} />
                  </Link>
                </li>
              ))}
            </ul>
          ) : feed.isError ? (
            <div className={styles.empty} role="alert">
              <p>{t("We couldn't load nearby activity.")}</p>
              <Button variant="ghost" size="sm" onClick={() => void feed.refetch()}>
                {t('Try again')}
              </Button>
            </div>
          ) : feed.data ? (
            <p className={styles.empty}>{t('Nothing in this category yet.')}</p>
          ) : null}
        </div>
        {feed.data && (feed.data.totalPages ?? 1) > 1 ? (
          <PaginationFooter
            page={feed.data.page ?? page}
            pageSize={feed.data.pageSize ?? 20}
            total={feed.data.total ?? items.length}
            totalPages={feed.data.totalPages ?? 1}
            pageSizeOptions={HOME_PAGE_SIZE}
            onPageChange={goToPage}
            onPageSizeChange={() => undefined}
            hidePageSize
          />
        ) : null}
        <Link to="/browse" className={styles.seeAll}>
          {t('See all nearby activity')}
        </Link>
      </section>
    </div>
  );
}
